import React from "react";
import { Form, Button, Mentions, Avatar } from "antd";
import { CommentaireService } from "../../services/CommentaireService";
import Toast from "../../helpers/Toast";
import PropTypes from "prop-types";
import { catchError } from "../../services/DaoService";
import CustomFormItem from "../utils/form/CustomFormItem";
import styles from "./CommentaireCreateForm.module.scss";
import UserService from "../../services/UserService";
import { useUserContext } from "../../contexts/UserContext";

const CommentaireCreateForm = ({ onCreate, target, parentId, users: usersProp }) => {
  const [form] = Form.useForm();
  const { currentUser } = useUserContext();
  const [creating, setCreating] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState({});
  const [users, setUsers] = React.useState(usersProp ?? []);
  const [loading, setLoading] = React.useState(!usersProp);

  React.useEffect(() => {
    if (usersProp) {
      setUsers(usersProp);
      return;
    }
    setLoading(true);
    UserService.getInstance()
      .all()
      .then((response) => setUsers(response.data))
      .catch(catchError)
      .finally(() => setLoading(false));
  }, [usersProp]);

  const handleSave = () => {
    setCreating(true);
    form
      .validateFields()
      .then((values) => {
        values.target = target;
        values.parent_id = parentId;
        CommentaireService.getInstance()
          .create(values)
          .then((response) => {
            Toast.success(response.message);
            form.resetFields();
            onCreate(response.data);
          })
          .catch((error) => {
            const errors = error.response?.data;
            if (errors?.validation && Object.keys(errors?.errors ?? {}).length) {
              setValidationErrors(errors.errors);
            } else {
              catchError(error);
            }
          })
          .finally(() => setCreating(false));
      })
      .catch(() => setCreating(false));
  };

  const mentionOptions = users.map((user) => ({
    value: user.email,
    label: (
      <span className={styles.mentionOptionLabel}>
        <Avatar src={user.avatar} size={18}>{user.name?.[0]}</Avatar>
        <span className={styles.mentionOptionName}>{user.name}</span>
        <span className={styles.mentionOptionEmail}>{user.email}</span>
      </span>
    ),
    name: user.name,
  }));

  return (
    <div className={styles.composeArea}>
      <div className={styles.composeAvatar}>
        <Avatar size={34} src={currentUser?.avatar}>
          {currentUser?.name?.[0]}
        </Avatar>
      </div>
      <div className={styles.composeBody}>
        <Form form={form} name="createCommentaireForm">
          <CustomFormItem
            name="comment"
            rules={[{ required: true, message: "Veuillez saisir un commentaire" }]}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            style={{ marginBottom: 6 }}
          >
            <Mentions
              rows={3}
              placeholder="Ajouter un commentaire… (@mention pour notifier un collaborateur)"
              className={styles.composeMentions}
              loading={loading}
              options={mentionOptions}
              filterOption={(input, option) => {
                const q = input.toLowerCase();
                return (
                  option.value.toLowerCase().includes(q) ||
                  (option.name ?? '').toLowerCase().includes(q)
                );
              }}
            />
          </CustomFormItem>
          <div className={styles.composeActions}>
            <Button type="primary" size="small" loading={creating} onClick={handleSave}>
              Poster
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default CommentaireCreateForm;

CommentaireCreateForm.propTypes = {
  onCreate: PropTypes.func.isRequired,
  target: PropTypes.string.isRequired,
  parentId: PropTypes.number.isRequired,
  users: PropTypes.array,
};
